import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./CreateTerminal.css";
import {
  BsBuilding,
  BsCheckCircleFill,
  BsArrowRight,
  BsArrowLeft,
  BsCheckLg,
  BsChevronDown,
  BsShieldFill,
  BsJournalText,
  BsPlusLg,
  BsDashLg,
  BsCreditCard2Front,
} from "react-icons/bs";

import { apiService } from "../../../../services/apiService";
import { API_PATHS } from "../../../../utils/apiPaths";
import { extractApiData, isApiSuccess } from "../../../../utils/helpers";
import SearchableSelect from "../../../../components/shared/SearchableSelect";
import CreateFlowHero from "../../../../components/shared/CreateFlowHero";
import Loader from "../../../../components/shared/Loader";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { Button } from "../../../../components/ui/button";
import { Checkbox } from "../../../../components/ui/checkbox";
import { cn } from "../../../../lib/utils";
import type { CreateTerminalEditState } from "../../models/types";

export type { CreateTerminalEditState };
/** @deprecated Use CreateTerminalEditState */
export type CreateMIDEditState = CreateTerminalEditState;

type MerchantOption = { id: number; name: string };
type AcquirerOption = { id: number; name: string };

function normalizeMerchantOptions(raw: unknown): MerchantOption[] {
  const payload = extractApiData<unknown>(raw);
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as { content?: unknown[] })?.content)
      ? (payload as { content: unknown[] }).content
      : [];

  return list
    .map((item) => {
      const row = item as Record<string, unknown>;
      return {
        id: Number(row.id ?? row.autoId ?? row.merchantId ?? 0),
        name: String(row.name ?? row.merchantName ?? row.companyName ?? "").trim(),
      };
    })
    .filter((merchant) => merchant.id > 0 && merchant.name.length > 0);
}

function normalizeAcquirerOptions(raw: unknown): AcquirerOption[] {
  const payload = extractApiData<unknown>(raw);
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as { content?: unknown[] })?.content)
      ? (payload as { content: unknown[] }).content
      : [];

  return list
    .map((item) => {
      const row = item as Record<string, unknown>;
      return {
        id: Number(row.id ?? row.autoId ?? row.acquirerId ?? 0),
        name: String(row.name ?? row.acquirerName ?? "").trim(),
      };
    })
    .filter((acquirer) => acquirer.id > 0 && acquirer.name.length > 0);
}

const STEPS = [
  { id: 1, label: "Terminal Access", icon: <BsShieldFill /> },
  { id: 2, label: "Configuration", icon: <BsBuilding /> },
  { id: 3, label: "Review & Submit", icon: <BsCheckLg /> },
];

const BLANK = {
  terminalFor: "", // 'merchant' or 'acquirer'
  merchantId: "",
  acquirerId: "",
  terminalName: "",
  trafficId: "",
  currencies: [] as number[],
  countryId: "",
  flowId: "",
  industryId: "",
  externalMerchantId: "", // To avoid confusion with our internal DB ID
  notes: "",
  settlementCurrency: "", // Acquirer terminal only: used by the automatic/live DCC feature.
  apmEnabled: false, // Acquirer terminal only: offers CollectNExchange APMs on hosted checkout.
  enforceRoutingConstraints: false, // Acquirer terminal only: MID protection opt-in.
  allowedCountryCodes: "", // Comma-separated ISO codes / "EU" / "*", raw text until submit.
  minAmount: "",
  maxAmount: "",
};

type ExtraFieldRow = { id: number; key: string; value: string };

const DEFAULT_EXTRA_FIELD_ROWS: ExtraFieldRow[] = [
  { id: 1, key: "", value: "" },
];

const buildExtraFields = (rows: ExtraFieldRow[]): Record<string, string> =>
  rows.reduce<Record<string, string>>((acc, row) => {
    const k = row.key.trim();
    if (k) acc[k] = row.value;
    return acc;
  }, {});

const parseAllowedCountryCodes = (raw: string): string[] =>
  raw
    .split(",")
    .map((code) => code.trim().toUpperCase())
    .filter((code) => code.length > 0);

function parseEditState(state: unknown): CreateTerminalEditState | null {
  if (!state || typeof state !== "object") return null;
  const row = state as Partial<CreateTerminalEditState>;
  if (row.mode !== "edit" || !row.terminalId) return null;
  if (row.terminalType !== "merchant" && row.terminalType !== "acquirer") return null;
  return {
    mode: "edit",
    terminalType: row.terminalType,
    terminalId: Number(row.terminalId),
  };
}

function parseNumberArray(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => Number(item)).filter((n) => !Number.isNaN(n));
}

function parseExtraFields(value: unknown): Record<string, string> {
  if (!value || typeof value !== "object") return {};
  return Object.entries(value as Record<string, unknown>).reduce<Record<string, string>>(
    (acc, [key, val]) => {
      acc[key] = String(val ?? "");
      return acc;
    },
    {},
  );
}

function mapTerminalApiToForm(
  item: Record<string, unknown>,
  terminalType: "merchant" | "acquirer",
) {
  return {
    ...BLANK,
    terminalFor: terminalType,
    merchantId: terminalType === "merchant" ? String(item.merchantId ?? "") : "",
    acquirerId: terminalType === "acquirer" ? String(item.acquirerId ?? "") : "",
    terminalName: String(item.name ?? item.terminalName ?? ""),
    countryId: item.countryId ? String(item.countryId) : "",
    currencies: parseNumberArray(item.currencies),
    flowId: item.flowId ? String(item.flowId) : "",
    industryId: item.industryId ? String(item.industryId) : "",
    trafficId: item.trafficId ? String(item.trafficId) : "",
    notes: String(item.notes ?? ""),
    settlementCurrency: String(item.settlementCurrency ?? ""),
    apmEnabled: Boolean(item.apmEnabled),
    enforceRoutingConstraints: Boolean(item.enforceRoutingConstraints),
    allowedCountryCodes: Array.isArray(item.allowedCountryCodes)
      ? (item.allowedCountryCodes as unknown[]).map((code) => String(code)).join(", ")
      : "",
    minAmount: item.minAmount != null ? String(item.minAmount) : "",
    maxAmount: item.maxAmount != null ? String(item.maxAmount) : "",
  };
}

function buildExtraFieldRowsFromRecord(
  fields?: Record<string, string>,
): ExtraFieldRow[] {
  const entries = Object.entries(fields ?? {}).filter(([key]) => key.trim());
  if (entries.length === 0) return [...DEFAULT_EXTRA_FIELD_ROWS];
  return entries.map(([key, value], idx) => ({ id: idx + 1, key, value }));
}

const CreateTerminal: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editState = useMemo(
    () => parseEditState(location.state),
    [location.state],
  );
  const isEditMode = editState != null;
  const [editingTerminalId, setEditingTerminalId] = useState<number | null>(
    editState?.terminalId ?? null,
  );

  const [step, setStep] = useState(isEditMode ? 2 : 1);
  const [form, setForm] = useState<any>({ ...BLANK });
  const [done, setDone] = useState(false);
  const [, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [merchants, setMerchants] = useState<MerchantOption[]>([]);
  const [acquirers, setAcquirers] = useState<AcquirerOption[]>([]);
  const [countryOptions, setCountryOptions] = useState<
    Array<{
      autoId: number;
      countryName: string;
      currency: string;
      countryFlag: string;
    }>
  >([]);
  const [flowOptions, setFlowOptions] = useState<
    Array<{ autoId: number; label: string }>
  >([]);
  const [industryOptions, setIndustryOptions] = useState<
    Array<{ autoId: number; label: string }>
  >([]);
  const [trafficOptions, setTrafficOptions] = useState<
    Array<{ autoId: number; label: string }>
  >([]);
  const [currencySearch, setCurrencySearch] = useState("");
  const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const hasInitialized = useRef(false);
  const currencyDropdownRef = useRef<HTMLDivElement>(null);
  const currencySearchInputRef = useRef<HTMLInputElement>(null);
  const extraFieldIdRef = useRef(2);
  const [extraFieldRows, setExtraFieldRows] = useState<ExtraFieldRow[]>([
    ...DEFAULT_EXTRA_FIELD_ROWS,
  ]);

  useEffect(() => {
    if (!editState) return;

    let cancelled = false;

    const fetchTerminalForEdit = async () => {
      setEditLoading(true);
      setApiError(null);
      try {
        const isMerchant = editState.terminalType === "merchant";
        const res: any = await apiService.post(
          isMerchant
            ? API_PATHS.GET_MERCHANT_TERMINAL_BY_ID
            : API_PATHS.GET_ACQUIRER_TERMINAL_BY_ID,
          { terminalId: editState.terminalId },
        );

        if (cancelled) return;

        if (res?.success === false) {
          throw new Error(res?.message || "Unable to load terminal.");
        }

        const item = extractApiData<Record<string, unknown>>(res);
        if (!item || typeof item !== "object") {
          throw new Error("Unable to load terminal.");
        }

        setForm(mapTerminalApiToForm(item, editState.terminalType));
        setEditingTerminalId(
          Number(
            item.terminalId ??
              item.id ??
              item.autoId ??
              item.merchantTerminalId ??
              item.acquirerTerminalId ??
              editState.terminalId,
          ) || editState.terminalId,
        );

        if (!isMerchant) {
          const rows = buildExtraFieldRowsFromRecord(parseExtraFields(item.extraFields));
          extraFieldIdRef.current = rows.length + 1;
          setExtraFieldRows(rows);
        }
      } catch (err: any) {
        if (cancelled) return;
        console.error("Failed to load terminal for edit:", err);
        setApiError(
          err.response?.data?.message ||
            err.message ||
            "Failed to load terminal. Please try again.",
        );
      } finally {
        if (!cancelled) setEditLoading(false);
      }
    };

    void fetchTerminalForEdit();
    return () => {
      cancelled = true;
    };
  }, [editState]);

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    fetchLists();
    fetchAcquirerTerminalLookups();
  }, []);

  const fetchLists = async () => {
    setLoading(true);
    setApiError(null);
    try {
      const [aRes, mRes] = await Promise.all([
        apiService.post(API_PATHS.GET_ACQUIRERS_BY_STATUS, {
          status: "ACTIVE",
        }),
        apiService.post(API_PATHS.GET_MERCHANTS_BY_STATUS, {
          status: "ACTIVE",
        }),
      ]);

      if (isApiSuccess(aRes)) {
        setAcquirers(normalizeAcquirerOptions(aRes));
      } else {
        setAcquirers([]);
      }

      if (isApiSuccess(mRes)) {
        setMerchants(normalizeMerchantOptions(mRes));
      } else {
        setMerchants([]);
        setApiError(
          (mRes as { message?: string })?.message ||
            "Unable to load active merchants.",
        );
      }
    } catch (err) {
      console.error("Failed to fetch active acquirers or merchants", err);
      setMerchants([]);
      setAcquirers([]);
      setApiError("Failed to load merchants. Please refresh and try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAcquirerTerminalLookups = async () => {
    try {
      const [countriesRes, flowsRes, industriesRes, trafficRes]: any =
        await Promise.all([
          apiService.get(API_PATHS.GET_ACTIVE_COUNTRIES),
          apiService.get(API_PATHS.GET_ACTIVE_FLOWS),
          apiService.get(API_PATHS.GET_ACTIVE_INDUSTRIES),
          apiService.get(API_PATHS.GET_ACTIVE_TRAFFIC),
        ]);

      const countriesRaw = Array.isArray(countriesRes?.data)
        ? countriesRes.data
        : Array.isArray(countriesRes)
          ? countriesRes
          : [];
      const countries = countriesRaw
        .map((c: any) => ({
          autoId: Number(c.autoId ?? c.id ?? 0),
          countryName: String(c.countryName ?? c.name ?? ""),
          currency: String(c.currency ?? "")
            .trim()
            .toUpperCase(),
          countryFlag: String(c.countryFlag ?? "")
            .trim()
            .toUpperCase(),
        }))
        .filter(
          (c: { autoId: number; countryName: string; currency: string }) =>
            c.autoId > 0 && c.countryName.length > 0 && c.currency.length > 0,
        );
      setCountryOptions(countries);

      const flowsRaw = Array.isArray(flowsRes?.data)
        ? flowsRes.data
        : Array.isArray(flowsRes)
          ? flowsRes
          : [];
      const flows = flowsRaw
        .map((f: any) => {
          const autoId = Number(f.autoId ?? f.id ?? f.flowId ?? 0);
          const label = String(
            f.type ??
              f.flowType ??
              f.name ??
              f.flowName ??
              (autoId ? `Flow ${autoId}` : ""),
          ).trim();
          return { autoId, label };
        })
        .filter(
          (f: { autoId: number; label: string }) =>
            f.autoId > 0 && f.label.length > 0,
        );
      setFlowOptions(flows);

      const industriesRaw = Array.isArray(industriesRes?.data)
        ? industriesRes.data
        : Array.isArray(industriesRes)
          ? industriesRes
          : [];
      const industries = industriesRaw
        .map((it: any, idx: number) => {
          const autoId = Number(it.autoId ?? it.id ?? idx + 1);
          const label = String(
            it.type ??
              it.name ??
              it.industryName ??
              it.industryType ??
              it.label ??
              "",
          ).trim();
          return { autoId, label };
        })
        .filter((it: { autoId: number; label: string }) => it.label.length > 0);
      setIndustryOptions(industries);

      const trafficRaw = Array.isArray(trafficRes?.data)
        ? trafficRes.data
        : Array.isArray(trafficRes)
          ? trafficRes
          : [];
      const traffic = trafficRaw
        .map((t: any, idx: number) => {
          const autoId = Number(t.autoId ?? t.id ?? idx + 1);
          const label = String(
            t.type ?? t.name ?? t.trafficName ?? t.trafficType ?? t.label ?? "",
          ).trim();
          return { autoId, label };
        })
        .filter((t: { autoId: number; label: string }) => t.label.length > 0);
      setTrafficOptions(traffic);
    } catch (err) {
      console.error("Failed to load acquirer terminal lookups", err);
    }
  };

  const set = (field: string, value: any) => {
    setForm((prev: any) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }) as any);
    }
  };

  const merchantSelectOptions = useMemo(
    () => merchants.map((m) => ({ value: String(m.id), label: m.name })),
    [merchants],
  );

  const acquirerSelectOptions = useMemo(
    () => acquirers.map((a) => ({ value: String(a.id), label: a.name })),
    [acquirers],
  );

  const countrySelectOptions = useMemo(
    () =>
      countryOptions.map((c) => ({
        value: String(c.autoId),
        label: c.countryName,
        searchText: `${c.countryName} ${c.countryFlag}`,
      })),
    [countryOptions],
  );

  const currencyDropdownRows = useMemo(
    () => {
      const byCode = new Map<string, { currency: string; autoId: number }>();
      for (const row of countryOptions) {
        const code = row.currency.toUpperCase();
        if (!byCode.has(code)) {
          byCode.set(code, { currency: row.currency, autoId: row.autoId });
        }
      }
      return [...byCode.values()].sort((a, b) =>
        a.currency.localeCompare(b.currency),
      );
    },
    [countryOptions],
  );

  const settlementCurrencySelectOptions = useMemo(
    () => currencyDropdownRows.map((row) => ({ value: row.currency, label: row.currency })),
    [currencyDropdownRows],
  );

  const filteredCurrencyOptions = useMemo(() => {
    const q = currencySearch.trim().toLowerCase();
    if (!q) return currencyDropdownRows;
    return currencyDropdownRows.filter((row) =>
      row.currency.toLowerCase().includes(q),
    );
  }, [currencyDropdownRows, currencySearch]);

  const flowSelectOptions = useMemo(
    () => flowOptions.map((f) => ({ value: String(f.autoId), label: f.label })),
    [flowOptions],
  );

  const industrySelectOptions = useMemo(
    () => industryOptions.map((i) => ({ value: String(i.autoId), label: i.label })),
    [industryOptions],
  );

  const trafficSelectOptions = useMemo(
    () => trafficOptions.map((t) => ({ value: String(t.autoId), label: t.label })),
    [trafficOptions],
  );

  const selectedCountryIds = useMemo<number[]>(
    () => [
      ...new Set(
        ((form.currencies || []) as number[])
          .map((id) => Number(id))
          .filter((id) => id > 0),
      ),
    ],
    [form.currencies],
  );

  const selectedCurrencyRows = useMemo(
    () =>
      selectedCountryIds
        .map((id) => countryOptions.find((c) => c.autoId === id))
        .filter(Boolean) as Array<{
        autoId: number;
        countryName: string;
        currency: string;
        countryFlag: string;
      }>,
    [selectedCountryIds, countryOptions],
  );

  const selectedCurrencyLabels = useMemo(
    () =>
      [...new Set(selectedCurrencyRows.map((c) => c.currency))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [selectedCurrencyRows],
  );

  const getCountryIdsForCurrency = (currency: string) =>
    countryOptions
      .filter((c) => c.currency.toUpperCase() === currency.toUpperCase())
      .map((c) => c.autoId);

  const isCurrencySelected = (currency: string) =>
    getCountryIdsForCurrency(currency).some((id) =>
      selectedCountryIds.includes(id),
    );

  const currencyTriggerLabel = useMemo(() => {
    if (!selectedCurrencyLabels.length) return "Select currencies";
    if (selectedCurrencyLabels.length <= 3)
      return selectedCurrencyLabels.join(", ");
    return `${selectedCurrencyLabels.length} currencies selected`;
  }, [selectedCurrencyLabels]);

  useEffect(() => {
    if (!currencyDropdownOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (
        currencyDropdownRef.current &&
        !currencyDropdownRef.current.contains(e.target as Node)
      ) {
        setCurrencyDropdownOpen(false);
        setCurrencySearch("");
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [currencyDropdownOpen]);

  useEffect(() => {
    if (currencyDropdownOpen) {
      currencySearchInputRef.current?.focus();
    } else {
      setCurrencySearch("");
    }
  }, [currencyDropdownOpen]);

  const handleToggleCurrency = (currency: string) => {
    const matchingIds = getCountryIdsForCurrency(currency);
    const anySelected = matchingIds.some((id) => selectedCountryIds.includes(id));

    const current = selectedCountryIds.filter((id) => !matchingIds.includes(id));
    if (!anySelected && matchingIds[0]) {
      set("currencies", [...current, matchingIds[0]]);
    } else {
      set("currencies", current);
    }

    if (errors.currencies) {
      setErrors((prev) => ({ ...prev, currencies: undefined }) as any);
    }
  };

  const handleRemoveCurrency = (currency: string) => {
    const matchingIds = new Set(getCountryIdsForCurrency(currency));
    set(
      "currencies",
      selectedCountryIds.filter((id) => !matchingIds.has(id)),
    );
    if (errors.currencies) {
      setErrors((prev) => ({ ...prev, currencies: undefined }) as any);
    }
  };

  const handleClearAllCurrencies = () => {
    set("currencies", []);
  };

  const addExtraFieldRow = () => {
    const id = extraFieldIdRef.current++;
    setExtraFieldRows((rows) => [...rows, { id, key: "", value: "" }]);
  };

  const removeExtraFieldRow = (id: number) => {
    setExtraFieldRows((rows) => {
      if (rows.length <= 1) return rows;
      return rows.filter((row) => row.id !== id);
    });
  };

  const updateExtraFieldRow = (
    id: number,
    field: "key" | "value",
    value: string,
  ) => {
    setExtraFieldRows((rows) =>
      rows.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    );
  };

  const addCountryCurrency = (countryId: string) => {
    const autoId = Number(countryId);
    if (!autoId) return;
    const row = countryOptions.find((c) => c.autoId === autoId);
    if (!row?.currency) return;

    const current: number[] = [...(form.currencies || [])];
    if (!current.includes(autoId)) set("currencies", [...current, autoId]);

    if (errors.currencies) {
      setErrors((prev) => ({ ...prev, currencies: undefined }) as any);
    }
  };

  const getStepErrors = (s: number): Record<string, string> => {
    const e: Record<string, string> = {};
    if (s === 1 || s === 3) {
      if (!form.terminalFor) e.terminalFor = "Please select a destination type";
    }
    if (s === 2 || s === 3) {
      if (form.terminalFor === "merchant") {
        if (!form.merchantId) e.merchantId = "Please select a merchant";
        if (!form.countryId) e.countryId = "Please select a country";
        if (!form.flowId) e.flowId = "Please select a flow";
      } else {
        if (!form.acquirerId) e.acquirerId = "Please select an acquirer";
        if (!form.countryId) e.countryId = "Please select a country";
        if (!form.flowId) e.flowId = "Please select a flow";
        if (!form.industryId) e.industryId = "Please select an industry";
        if (!form.trafficId) e.trafficId = "Please select a traffic type";
        if (!selectedCountryIds.length)
          e.currencies = "Select at least one currency";
      }
      if (!form.terminalName.trim())
        e.terminalName = "Terminal name is required";
      if (form.terminalFor === "merchant" && !selectedCountryIds.length) {
        e.currencies = "Select at least one currency";
      }
    }
    return e;
  };

  const validate = (s: number): boolean => {
    const e = getStepErrors(s);
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const getFirstInvalidStep = (): number | null => {
    if (Object.keys(getStepErrors(1)).length > 0) return 1;
    if (Object.keys(getStepErrors(2)).length > 0) return 2;
    return null;
  };

  const next = () => {
    if (validate(step)) setStep((s) => s + 1);
  };
  const back = () => {
    setErrors({});
    if (isEditMode && step === 2) {
      navigate("/dashboard/terminals");
      return;
    }
    setStep((s) => s - 1);
  };

  const submitMID = async () => {
    const stepErrors = getStepErrors(3);
    if (Object.keys(stepErrors).length > 0) {
      const invalidStep = getFirstInvalidStep();
      if (invalidStep) setStep(invalidStep);
      setErrors(stepErrors);
      return;
    }

    if (isEditMode && !editingTerminalId) {
      setApiError("Terminal ID is missing. Please go back and try again.");
      return;
    }

    setApiError(null);
    setErrors({});
    setSubmitting(true);
    try {
      const response: any = isEditMode
        ? form.terminalFor === "acquirer"
          ? await apiService.post(API_PATHS.UPDATE_ACQUIRER_TERMINAL, {
              terminalId: editingTerminalId,
              acquirerId: Number(form.acquirerId),
              name: form.terminalName.trim(),
              currencies: selectedCountryIds,
              countryId: Number(form.countryId),
              industryId: Number(form.industryId),
              flowId: Number(form.flowId),
              trafficId: Number(form.trafficId),
              extraFields: buildExtraFields(extraFieldRows),
              settlementCurrency: form.settlementCurrency?.trim() || null,
              apmEnabled: form.apmEnabled,
              enforceRoutingConstraints: form.enforceRoutingConstraints,
              allowedCountryCodes: parseAllowedCountryCodes(form.allowedCountryCodes),
              minAmount: form.minAmount.trim() ? Number(form.minAmount) : null,
              maxAmount: form.maxAmount.trim() ? Number(form.maxAmount) : null,
              notes: form.notes?.trim() || null,
            })
          : await apiService.post(API_PATHS.UPDATE_MERCHANT_TERMINAL, {
              terminalId: editingTerminalId,
              merchantId: Number(form.merchantId),
              name: form.terminalName.trim(),
              notes: form.notes?.trim() || "",
              currencies: selectedCountryIds,
              countryId: Number(form.countryId),
              flowId: Number(form.flowId),
              industryId: Number(form.industryId),
              trafficId: Number(form.trafficId),
            })
        : form.terminalFor === "acquirer"
          ? await apiService.post(API_PATHS.ADD_ACQUIRER_TERMINAL, {
              acquirerId: Number(form.acquirerId),
              name: form.terminalName.trim(),
              currencies: selectedCountryIds,
              countryId: Number(form.countryId),
              industryId: Number(form.industryId),
              flowId: Number(form.flowId),
              trafficId: Number(form.trafficId),
              extraFields: buildExtraFields(extraFieldRows),
              settlementCurrency: form.settlementCurrency?.trim() || null,
              apmEnabled: form.apmEnabled,
              enforceRoutingConstraints: form.enforceRoutingConstraints,
              allowedCountryCodes: parseAllowedCountryCodes(form.allowedCountryCodes),
              minAmount: form.minAmount.trim() ? Number(form.minAmount) : null,
              maxAmount: form.maxAmount.trim() ? Number(form.maxAmount) : null,
              notes: form.notes?.trim() || null,
            })
          : await apiService.post(API_PATHS.ADD_MERCHANT_TERMINAL, {
              merchantId: Number(form.merchantId),
              name: form.terminalName.trim(),
              notes: form.notes?.trim() || "",
              currencies: selectedCountryIds,
              countryId: Number(form.countryId),
              flowId: Number(form.flowId),
              ...(form.industryId
                ? { industryId: Number(form.industryId) }
                : {}),
              ...(form.trafficId ? { trafficId: Number(form.trafficId) } : {}),
            });

      if (
        response &&
        typeof response === "object" &&
        "success" in response &&
        !isApiSuccess(response)
      ) {
        setApiError(
          response?.message ||
            (isEditMode
              ? "Failed to update terminal. Please try again."
              : "Failed to create terminal. Please try again."),
        );
        return;
      }

      setSubmitMessage(
        response?.message ||
          (isEditMode
            ? "Terminal has been updated successfully."
            : "Terminal has been submitted successfully."),
      );
      setDone(true);
    } catch (err: any) {
      console.error("Submission failed", err);
      setApiError(
        err.response?.data?.message ||
          (isEditMode
            ? "Failed to update terminal. Please try again."
            : "Failed to create terminal. Please try again."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step < 3) {
      next();
    }
  };

  const handleReset = () => {
    setForm({ ...BLANK });
    setStep(1);
    setDone(false);
    setSubmitMessage(null);
    setErrors({});
    setApiError(null);
    setCurrencySearch("");
    setCurrencyDropdownOpen(false);
    extraFieldIdRef.current = 2;
    setExtraFieldRows([...DEFAULT_EXTRA_FIELD_ROWS]);
  };

  if (done) {
    const targetName =
      form.terminalFor === "merchant"
        ? merchants.find((m) => m.id === Number(form.merchantId))?.name
        : acquirers.find((a) => a.id === Number(form.acquirerId))?.name;

    return (
      <div className="cm-page">
        <div className="cm-success-wrap">
          <div className="cm-success-icon">
            <BsCheckCircleFill />
          </div>
          <h2 className="cm-success-title">
            {isEditMode ? "Terminal Updated!" : "Terminal Submitted!"}
          </h2>
          <p className="cm-success-sub">
            {submitMessage || (
              <>
                The configuration for{" "}
                <strong>{targetName || "the selected entity"}</strong> has been{" "}
                {isEditMode ? "updated" : "submitted"}.
              </>
            )}
          </p>
          <div className="cm-success-details">
            <div className="cm-sdet">
              <span>Type</span>
              <strong>
                {form.terminalFor === "merchant"
                  ? "Merchant Terminal"
                  : "Acquirer Terminal"}
              </strong>
            </div>
            <div className="cm-sdet">
              <span>Terminal</span>
              <strong>{form.terminalName}</strong>
            </div>
            <div className="cm-sdet">
              <span>Industry</span>
              <strong>
                {industryOptions.find(
                  (i) => String(i.autoId) === String(form.industryId),
                )?.label || "—"}
              </strong>
            </div>
            <div className="cm-sdet">
              <span>Currencies</span>
              <strong>{selectedCurrencyLabels.join(", ") || "—"}</strong>
            </div>
          </div>
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/dashboard/terminals")}
            >
              Back to Terminals
            </Button>
            {!isEditMode && (
              <Button type="button" onClick={handleReset}>
                Create Another Terminal
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cm-page dh-page">
      <CreateFlowHero
        badge={isEditMode ? "Edit Terminal" : "New Terminal"}
        title={isEditMode ? "Edit Terminal" : "Create Terminal"}
        subtitle={
          isEditMode
            ? "Update merchant or acquirer terminal configuration"
            : "Configure and link a new merchant or acquirer terminal"
        }
        icon={<BsCreditCard2Front />}
        onBack={() => navigate("/dashboard/terminals")}
        backLabel="Back to Terminals"
      />

      {/* Stepper */}
      <div className="cm-stepper">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            <div
              className={`cm-step ${step === s.id ? "active" : step > s.id ? "done" : ""}`}
            >
              <div className="cm-step-circle">
                {step > s.id ? <BsCheckLg /> : s.icon}
              </div>
              <span className="cm-step-label">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`cm-step-line ${step > s.id ? "done" : ""}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Form Card */}
      <div className="cm-card" style={{ position: "relative" }}>
        {editLoading ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "300px",
            }}
          >
            <Loader size="lg" label="Loading terminal details…" />
          </div>
        ) : (
        <form onSubmit={handleSubmit}>
          {/* ── Step 1: Terminal Destination ── */}
          {step === 1 && (
            <div className="cm-step-body">
              <h3 className="cm-step-title">
                <BsShieldFill /> Terminal Destination
              </h3>
              <p className="cm-step-sub" style={{ marginBottom: "2rem" }}>
                Choose who this terminal configuration is being created for
              </p>

              <div
                className="cm-grid"
                style={{ gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}
              >
                <div
                  className={`cm-type-card ${form.terminalFor === "merchant" ? "active" : ""}`}
                  onClick={() => set("terminalFor", "merchant")}
                >
                  <div className="cm-type-icon">
                    <BsBuilding />
                  </div>
                  <div className="cm-type-name">Merchant Terminal</div>
                  <div className="cm-type-desc">
                    For processing traffic on behalf of a specific merchant
                  </div>
                </div>

                <div
                  className={`cm-type-card ${form.terminalFor === "acquirer" ? "active" : ""}`}
                  onClick={() => set("terminalFor", "acquirer")}
                >
                  <div className="cm-type-icon">
                    <BsCheckLg />
                  </div>
                  <div className="cm-type-name">Acquirer Terminal</div>
                  <div className="cm-type-desc">
                    Direct credential setup for an acquirer bank connection
                  </div>
                </div>
              </div>
              {errors.terminalFor && (
                <p
                  className="cm-err"
                  style={{ textAlign: "center", marginTop: "1rem" }}
                >
                  {errors.terminalFor}
                </p>
              )}
            </div>
          )}

          {/* ── Step 2: Configuration ── */}
          {step === 2 && (
            <div
              className={`cm-step-body${currencyDropdownOpen ? " cm-step-dropdown-open" : ""}`}
            >
              <h3 className="cm-step-title">
                {form.terminalFor === "merchant" ? (
                  <>
                    <BsBuilding /> Merchant Configuration{" "}
                  </>
                ) : (
                  <>
                    <BsCheckLg /> Acquirer Configuration
                  </>
                )}
              </h3>

              <div className="cm-grid">
                {form.terminalFor === "merchant" ? (
                  <label className="cm-label">
                    <span>
                      Select Merchant <em>*</em>
                    </span>
                    <SearchableSelect
                      className="h-9"
                      value={form.merchantId}
                      options={merchantSelectOptions}
                      onChange={(value) => set("merchantId", value)}
                      placeholder="Choose a merchant"
                      searchPlaceholder="Search merchant…"
                      emptyMessage="No active merchants available"
                      error={!!errors.merchantId}
                      aria-label="Merchant"
                    />
                    {errors.merchantId && (
                      <p className="cm-err">{errors.merchantId}</p>
                    )}
                  </label>
                ) : (
                  <label className="cm-label">
                    <span>
                      Select Acquirer <em>*</em>
                    </span>
                    <SearchableSelect
                      className="h-9"
                      value={form.acquirerId}
                      options={acquirerSelectOptions}
                      onChange={(value) => set("acquirerId", value)}
                      placeholder="Choose an acquirer"
                      searchPlaceholder="Search acquirer…"
                      emptyMessage="No acquirers available"
                      error={!!errors.acquirerId}
                      aria-label="Acquirer"
                    />
                    {errors.acquirerId && (
                      <p className="cm-err">{errors.acquirerId}</p>
                    )}
                  </label>
                )}

                <label className="cm-label">
                  <span>
                    Terminal Name <em>*</em>
                  </span>
                  <Input
                    className={cn(errors.terminalName && "border-destructive")}
                    value={form.terminalName}
                    onChange={(e) => set("terminalName", e.target.value)}
                    placeholder="e.g. Primary Mid"
                  />
                  {errors.terminalName && (
                    <p className="cm-err">{errors.terminalName}</p>
                  )}
                </label>

                {form.terminalFor === "merchant" && (
                  <>
                    <label className="cm-label">
                      <span>
                        Country <em>*</em>
                      </span>
                      <SearchableSelect
                        className="h-9"
                        value={form.countryId}
                        options={countrySelectOptions}
                        onChange={(value) => {
                          set("countryId", value);
                          addCountryCurrency(value);
                        }}
                        placeholder="Select country"
                        searchPlaceholder="Search country…"
                        emptyMessage="No countries available"
                        error={!!errors.countryId}
                        aria-label="Country"
                      />
                      {errors.countryId && (
                        <p className="cm-err">{errors.countryId}</p>
                      )}
                    </label>

                    <label className="cm-label">
                      <span>
                        Flow <em>*</em>
                      </span>
                      <SearchableSelect
                        className="h-9"
                        value={form.flowId}
                        options={flowSelectOptions}
                        onChange={(value) => set("flowId", value)}
                        placeholder="Select flow"
                        searchPlaceholder="Search flow…"
                        emptyMessage="No flows available"
                        error={!!errors.flowId}
                        aria-label="Flow"
                      />
                      {errors.flowId && (
                        <p className="cm-err">{errors.flowId}</p>
                      )}
                    </label>

                    <label className="cm-label">
                      <span>Industry</span>
                      <SearchableSelect
                        className="h-9"
                        value={form.industryId}
                        options={industrySelectOptions}
                        onChange={(value) => set("industryId", value)}
                        placeholder="Select industry"
                        searchPlaceholder="Search industry…"
                        emptyMessage="No industries available"
                        aria-label="Industry"
                      />
                    </label>

                    <label className="cm-label">
                      <span>Traffic Type</span>
                      <SearchableSelect
                        className="h-9"
                        value={form.trafficId}
                        options={trafficSelectOptions}
                        onChange={(value) => set("trafficId", value)}
                        placeholder="Select traffic type"
                        searchPlaceholder="Search traffic type…"
                        emptyMessage="No traffic types available"
                        aria-label="Traffic type"
                      />
                    </label>
                  </>
                )}

                {form.terminalFor === "acquirer" && (
                  <>
                    <label className="cm-label">
                      <span>
                        Country <em>*</em>
                      </span>
                      <SearchableSelect
                        className="h-9"
                        value={form.countryId}
                        options={countrySelectOptions}
                        onChange={(value) => {
                          set("countryId", value);
                          addCountryCurrency(value);
                        }}
                        placeholder="Select country"
                        searchPlaceholder="Search country…"
                        emptyMessage="No countries available"
                        error={!!errors.countryId}
                        aria-label="Country"
                      />
                      {errors.countryId && (
                        <p className="cm-err">{errors.countryId}</p>
                      )}
                    </label>

                    <label className="cm-label">
                      <span>
                        Flow <em>*</em>
                      </span>
                      <SearchableSelect
                        className="h-9"
                        value={form.flowId}
                        options={flowSelectOptions}
                        onChange={(value) => set("flowId", value)}
                        placeholder="Select flow"
                        searchPlaceholder="Search flow…"
                        emptyMessage="No flows available"
                        error={!!errors.flowId}
                        aria-label="Flow"
                      />
                      {errors.flowId && (
                        <p className="cm-err">{errors.flowId}</p>
                      )}
                    </label>

                    <label className="cm-label">
                      <span>
                        Industry <em>*</em>
                      </span>
                      <SearchableSelect
                        className="h-9"
                        value={form.industryId}
                        options={industrySelectOptions}
                        onChange={(value) => set("industryId", value)}
                        placeholder="Select industry"
                        searchPlaceholder="Search industry…"
                        emptyMessage="No industries available"
                        error={!!errors.industryId}
                        aria-label="Industry"
                      />
                      {errors.industryId && (
                        <p className="cm-err">{errors.industryId}</p>
                      )}
                    </label>
                    <label className="cm-label">
                      <span>
                        Traffic Type <em>*</em>
                      </span>
                      <SearchableSelect
                        className="h-9"
                        value={form.trafficId}
                        options={trafficSelectOptions}
                        onChange={(value) => set("trafficId", value)}
                        placeholder="Select traffic type"
                        searchPlaceholder="Search traffic type…"
                        emptyMessage="No traffic types available"
                        error={!!errors.trafficId}
                        aria-label="Traffic type"
                      />
                      {errors.trafficId && (
                        <p className="cm-err">{errors.trafficId}</p>
                      )}
                    </label>

                    <label className="cm-label">
                      <span>Settlement Currency</span>
                      <SearchableSelect
                        className="h-9"
                        value={form.settlementCurrency}
                        options={settlementCurrencySelectOptions}
                        onChange={(value) => set("settlementCurrency", value)}
                        placeholder="Select settlement currency"
                        searchPlaceholder="Search currency…"
                        emptyMessage="No currencies available"
                        aria-label="Settlement currency"
                        clearable
                      />
                      <p className="cm-hint">
                        Used only by automatic DCC the one currency this terminal settles in.
                        Optional; leave unset if this terminal isn't used for automatic conversion.
                      </p>
                    </label>

                    <label className="cm-label">
                      <span className="flex items-center gap-2">
                        <Checkbox
                          checked={form.apmEnabled}
                          onCheckedChange={(checked) => set("apmEnabled", checked === true)}
                          aria-label="Enable Alternative Payment Methods"
                        />
                        Enable Alternative Payment Methods (APM)
                      </span>
                      <p className="cm-hint">
                        Offers CollectNExchange APMs (iDEAL, EPS, Bancontact, BLIK, Apple Pay) on
                        the hosted checkout page for sessions routed through this terminal.
                      </p>
                    </label>

                    <label className="cm-label">
                      <span className="flex items-center gap-2">
                        <Checkbox
                          checked={form.enforceRoutingConstraints}
                          onCheckedChange={(checked) => set("enforceRoutingConstraints", checked === true)}
                          aria-label="Enforce routing protection"
                        />
                        Enforce Routing Protection (MID protection)
                      </span>
                      <p className="cm-hint">
                        When on, this terminal only accepts payments matching the allowed
                        countries/currencies and amount range below enforced for every merchant
                        routed here, regardless of priority routing or Payment Routing rules. This
                        is a hard restriction, not a preference: it removes this terminal from
                        consideration entirely before Payment Routing rules ever run, so a rule
                        pointing at it won't help outside these bounds. Off by default; existing
                        terminals are unaffected until this is switched on.
                      </p>
                    </label>

                    {form.enforceRoutingConstraints && (
                      <>
                        <label className="cm-label">
                          <span>Allowed Countries</span>
                          <input
                            className="cm-input"
                            value={form.allowedCountryCodes}
                            onChange={(e) => set("allowedCountryCodes", e.target.value)}
                            placeholder="DE, FR, EU"
                          />
                          <p className="cm-hint">
                            Comma-separated 2-letter ISO codes, &quot;EU&quot; for the euro area,
                            or &quot;*&quot; for any. Leave blank to allow any country (only
                            currency/amount below are enforced).
                          </p>
                        </label>

                        <div className="cm-span2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                          <label className="cm-label">
                            <span>Min Amount</span>
                            <input
                              className="cm-input"
                              type="number"
                              min="0"
                              value={form.minAmount}
                              onChange={(e) => set("minAmount", e.target.value)}
                              placeholder="No lower bound"
                            />
                          </label>
                          <label className="cm-label">
                            <span>Max Amount</span>
                            <input
                              className="cm-input"
                              type="number"
                              min="0"
                              value={form.maxAmount}
                              onChange={(e) => set("maxAmount", e.target.value)}
                              placeholder="No upper bound"
                            />
                          </label>
                        </div>
                        <p className="cm-hint cm-span2">
                          Currency restriction reuses the Currencies field below the payment's
                          currency must be one of those selected.
                        </p>
                      </>
                    )}
                  </>
                )}

                <div
                  className={`cm-span2${currencyDropdownOpen ? " cm-currency-field-open" : ""}`}
                >
                  <span className="cm-label">
                    <span>
                      Currencies <em>*</em>
                    </span>
                    {selectedCountryIds.length > 0 && (
                      <span className="cm-currency-count">
                        {" "}
                        ({selectedCountryIds.length} selected)
                      </span>
                    )}
                  </span>
                  <p
                    className="cm-step-sub"
                    style={{ margin: "0.25rem 0 0.5rem" }}
                  >
                    Select one or more currencies.
                    Choosing a country above also adds its currency.
                  </p>

                  {selectedCurrencyLabels.length > 0 && (
                    <div className="cm-currency-selected">
                      {selectedCurrencyLabels.map((currency) => (
                        <span key={currency} className="cm-currency-chip">
                          {currency}
                          <button
                            type="button"
                            aria-label={`Remove ${currency}`}
                            onClick={() => handleRemoveCurrency(currency)}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  <div
                    className={`cm-currency-dropdown${currencyDropdownOpen ? " open" : ""}`}
                    ref={currencyDropdownRef}
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      className={`cm-currency-trigger cm-input ${errors.currencies ? "err" : ""} ${currencyDropdownOpen ? "open" : ""}`}
                      aria-haspopup="listbox"
                      aria-expanded={currencyDropdownOpen}
                      onClick={() =>
                        setCurrencyDropdownOpen((open) => !open)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setCurrencyDropdownOpen((open) => !open);
                        }
                      }}
                    >
                      <span
                        className={
                          selectedCountryIds.length
                            ? "cm-currency-trigger-value"
                            : "cm-currency-trigger-placeholder"
                        }
                      >
                        {currencyTriggerLabel}
                      </span>
                      <span className="cm-currency-trigger-actions">
                        {selectedCountryIds.length > 0 && (
                          <button
                            type="button"
                            className="cm-currency-trigger-clear"
                            aria-label="Clear all currencies"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClearAllCurrencies();
                            }}
                          >
                            ×
                          </button>
                        )}
                        <BsChevronDown
                          className={`cm-currency-trigger-chevron ${currencyDropdownOpen ? "open" : ""}`}
                          aria-hidden
                        />
                      </span>
                    </div>

                    {currencyDropdownOpen && (
                      <div className="cm-currency-menu">
                        <div className="cm-currency-menu-search-wrap">
                          <input
                            ref={currencySearchInputRef}
                            type="text"
                            className="cm-currency-menu-search"
                            value={currencySearch}
                            onChange={(e) =>
                              setCurrencySearch(e.target.value)
                            }
                            placeholder="Search currency…"
                            onKeyDown={(e) => e.stopPropagation()}
                          />
                        </div>
                        {countryOptions.length === 0 ? (
                          <p className="cm-currency-menu-empty">
                            No currencies loaded. Check active countries API.
                          </p>
                        ) : filteredCurrencyOptions.length === 0 ? (
                          <p className="cm-currency-menu-empty">
                            No currencies match your search.
                          </p>
                        ) : (
                          <ul
                            className="cm-currency-menu-list"
                            role="listbox"
                            aria-multiselectable
                          >
                            {filteredCurrencyOptions.map((row) => {
                              const isSelected = isCurrencySelected(row.currency);
                              return (
                                <li
                                  key={row.currency}
                                  role="option"
                                  aria-selected={isSelected}
                                  className={`cm-currency-menu-item ${isSelected ? "selected" : ""}`}
                                  onClick={() =>
                                    handleToggleCurrency(row.currency)
                                  }
                                >
                                  <span>{row.currency}</span>
                                  {isSelected && (
                                    <BsCheckLg aria-hidden />
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                  {errors.currencies && (
                    <p className="cm-err">{errors.currencies}</p>
                  )}
                </div>

                {form.terminalFor === "acquirer" && (
                  <>
                    <div
                      className="cm-section-sep"
                      style={{ gridColumn: "span 2" }}
                    />
                    <div className="cm-label cm-span2 cm-extra-fields">
                      <div className="cm-extra-fields-header">
                        <span>Integration Fields</span>
                        <button
                          type="button"
                          className="cm-extra-icon-btn cm-extra-add"
                          onClick={addExtraFieldRow}
                          aria-label="Add integration field row"
                          title="Add row"
                        >
                          <BsPlusLg />
                        </button>
                      </div>
                      <div className="cm-extra-fields-list">
                        {extraFieldRows.map((row) => (
                          <div key={row.id} className="cm-extra-field-row">
                            <Input
                              className={cn("flex-1 min-w-0")}
                              value={row.key}
                              onChange={(e) =>
                                updateExtraFieldRow(
                                  row.id,
                                  "key",
                                  e.target.value,
                                )
                              }
                              placeholder="Key"
                            />
                            <Input
                              className={cn("flex-1 min-w-0")}
                              value={row.value}
                              onChange={(e) =>
                                updateExtraFieldRow(
                                  row.id,
                                  "value",
                                  e.target.value,
                                )
                              }
                              placeholder="Value"
                            />
                            <button
                              type="button"
                              className="cm-extra-icon-btn cm-extra-remove"
                              onClick={() => removeExtraFieldRow(row.id)}
                              disabled={extraFieldRows.length <= 1}
                              aria-label="Remove extra field row"
                              title="Remove row"
                            >
                              <BsDashLg />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                <label className="cm-label cm-span2">
                  <span>Notes</span>
                  <Textarea
                    value={form.notes}
                    onChange={(e) => set("notes", e.target.value)}
                    placeholder="Internal remarks about this terminal configuration..."
                    rows={4}
                  />
                </label>
              </div>
            </div>
          )}

          {/* ── Step 3: Review ── */}
          {step === 3 && (
            <div className="cm-step-body">
              <h3 className="cm-step-title">
                <BsShieldFill /> Review &amp; Submit
              </h3>
              <div className="cm-review-grid">
                <div className="cm-review-section">
                  <div className="cm-review-section-title">
                    <BsShieldFill /> Terminal Access
                  </div>
                  {[
                    [
                      "Terminal Type",
                      form.terminalFor === "merchant" ? "Merchant" : "Acquirer",
                    ],
                    form.terminalFor === "merchant"
                      ? [
                          "Merchant",
                          merchants.find(
                            (m) => m.id === Number(form.merchantId),
                          )?.name || "—",
                        ]
                      : [
                          "Acquirer",
                          acquirers.find(
                            (a) => a.id === Number(form.acquirerId),
                          )?.name || "—",
                        ],
                    ["Terminal Name", form.terminalName || "—"],
                  ].map(([k, v]) => (
                    <div key={k} className="cm-review-row">
                      <span>{k}</span>
                      <strong>{v}</strong>
                    </div>
                  ))}
                </div>
                <div className="cm-review-section">
                  <div className="cm-review-section-title">
                    <BsBuilding /> Configuration
                  </div>
                  {[
                    [
                      "Country",
                      countryOptions.find(
                        (c) => String(c.autoId) === String(form.countryId),
                      )?.countryName || "—",
                    ],
                    [
                      "Flow",
                      flowOptions.find(
                        (f) => String(f.autoId) === String(form.flowId),
                      )?.label || "—",
                    ],
                    [
                      "Industry",
                      industryOptions.find(
                        (i) => String(i.autoId) === String(form.industryId),
                      )?.label || "—",
                    ],
                    [
                      "Traffic Type",
                      trafficOptions.find(
                        (t) => String(t.autoId) === String(form.trafficId),
                      )?.label || "—",
                    ],
                    ["Currencies", selectedCurrencyLabels.join(", ") || "—"],
                    ...(form.terminalFor === "acquirer" && form.enforceRoutingConstraints
                      ? [
                          ["Routing Protection", "Enforced"],
                          ["Allowed Countries", parseAllowedCountryCodes(form.allowedCountryCodes).join(", ") || "Any"],
                          ["Amount Range", `${form.minAmount.trim() || "0"} - ${form.maxAmount.trim() || "no limit"}`],
                        ]
                      : []),
                  ].map(([k, v]) => (
                    <div key={k} className="cm-review-row">
                      <span>{k}</span>
                      <strong>{v}</strong>
                    </div>
                  ))}

                  {form.terminalFor === "acquirer" &&
                    Object.keys(buildExtraFields(extraFieldRows)).length > 0 && (
                      <>
                        <div
                          className="cm-review-section-title"
                          style={{ marginTop: "1.25rem" }}
                        >
                          Extra Fields
                        </div>
                        {Object.entries(buildExtraFields(extraFieldRows)).map(
                          ([k, v]) => (
                            <div key={k} className="cm-review-row">
                              <span>{k}</span>
                              <strong>{v || "—"}</strong>
                            </div>
                          ),
                        )}
                      </>
                    )}

                  <div
                    className="cm-review-section-title"
                    style={{ marginTop: "1.25rem" }}
                  >
                    <BsJournalText /> Remarks
                  </div>
                  <div className="cm-review-row">
                    <span>Notes</span>
                    <strong>{form.notes?.trim() || "No notes added"}</strong>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="cm-nav">
            {step > 1 ? (
              <Button type="button" variant="outline" onClick={back}>
                <BsArrowLeft /> Back
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/dashboard/terminals")}
              >
                Cancel
              </Button>
            )}
            <div className="flex-1" />
            {step < 3 ? (
              <Button type="button" onClick={next}>
                {step === 2 ? (
                  <>
                    Review <BsArrowRight />
                  </>
                ) : (
                  <>
                    Continue <BsArrowRight />
                  </>
                )}
              </Button>
            ) : (
              <Button
                type="button"
                disabled={submitting}
                onClick={submitMID}
              >
                {submitting ? (
                  isEditMode ? "Saving..." : "Submitting..."
                ) : (
                  <>
                    <BsCheckCircleFill />{" "}
                    {isEditMode ? "Save Changes" : "Submit Terminal"}
                  </>
                )}
              </Button>
            )}
          </div>

          {apiError && (
            <div
              className="cm-api-err"
              style={{
                marginTop: "1rem",
                padding: "0.75rem",
                background: "rgba(239, 68, 68, 0.1)",
                color: "var(--color-error)",
                borderRadius: "8px",
                fontSize: "0.9rem",
                textAlign: "center",
              }}
            >
              {apiError}
            </div>
          )}
        </form>
        )}
        {editLoading && apiError && (
          <div
            className="cm-api-err"
            style={{
              marginTop: "1rem",
              padding: "0.75rem",
              background: "rgba(239, 68, 68, 0.1)",
              color: "var(--color-error)",
              borderRadius: "8px",
              fontSize: "0.9rem",
              textAlign: "center",
            }}
          >
            {apiError}
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateTerminal;
